import { memo, useCallback } from 'react';
import { Switch } from '@headlessui/react';
import { classnames } from '@/libs/utils';
import RangeSlider from '@/components/atoms/RangeSlider';

interface Props {
  showAfter?: number;
  hideAfter?: number;
  showAgainOnNewSpeaker?: boolean;
  onShowAfterChange: (value: number) => void;
  onHideAfterChange: (value: number | undefined) => void;
  onShowAgainOnNewSpeakerChange: (value: boolean) => void;
}

const TimingControls = memo(
  ({
    showAfter = 0,
    hideAfter,
    showAgainOnNewSpeaker = true,
    onShowAfterChange,
    onHideAfterChange,
    onShowAgainOnNewSpeakerChange
  }: Props) => {
    const isHideAfterEnabled = hideAfter !== undefined;

    const handleShowAfterChange = useCallback(
      (value: number) => {
        if (!isNaN(value) && value >= 0) {
          onShowAfterChange(value);
        }
      },
      [onShowAfterChange]
    );

    const handleHideToggle = useCallback(
      (enabled: boolean) => {
        if (enabled) {
          // Enable hide with default value of 3 seconds
          onHideAfterChange(3);
        } else {
          // Disable hide by setting to undefined
          onHideAfterChange(undefined);
        }
      },
      [onHideAfterChange]
    );

    const handleHideAfterChange = useCallback(
      (value: number) => {
        if (value === 0 && !isHideAfterEnabled) {
          onHideAfterChange(undefined);
        } else if (!isNaN(value) && value >= 0) {
          onHideAfterChange(value);
        }
      },
      [onHideAfterChange, isHideAfterEnabled]
    );

    const handleShowAgainChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onShowAgainOnNewSpeakerChange(e.target.checked);
      },
      [onShowAgainOnNewSpeakerChange]
    );

    return (
      <div className="flex flex-col gap-4">
        <RangeSlider
          id="show-after-slider"
          testId="show-after-slider"
          label="Show after..."
          min={0}
          max={10}
          step={1}
          value={showAfter}
          onChange={handleShowAfterChange}
          showValue
        />

        <div className="flex flex-col gap-3 rounded-lg border p-2">
          <div className="flex items-center justify-between">
            <label htmlFor="hide-toggle" className="text-xs font-medium text-gray-700">
              Hide after...
            </label>
            <Switch
              id="hide-toggle"
              data-testid="hide-toggle"
              checked={isHideAfterEnabled}
              onChange={handleHideToggle}
              className={classnames(
                'relative inline-flex h-5 w-8 items-center rounded-full transition-colors focus:outline-none focus:ring-offset-2',
                isHideAfterEnabled ? 'bg-black' : 'bg-gray-200'
              )}
            >
              <span
                className={classnames(
                  'inline-block size-4 rounded-full bg-white transition-transform',
                  isHideAfterEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                )}
              />
            </Switch>
          </div>

          {isHideAfterEnabled && (
            <>
              <div className="mt-1">
                <RangeSlider
                  id="hide-after-slider"
                  testId="hide-after-slider"
                  min={1}
                  max={15}
                  step={1}
                  value={hideAfter ?? 0}
                  onChange={handleHideAfterChange}
                  showValue
                />
              </div>
              <div className="flex items-start gap-2">
                <input
                  id="show-again-checkbox"
                  data-testid="show-again-checkbox"
                  type="checkbox"
                  checked={showAgainOnNewSpeaker}
                  onChange={handleShowAgainChange}
                  className="mt-0.5 size-4 cursor-pointer rounded border border-gray-300 text-black focus:ring-black"
                />
                <div className="flex flex-col">
                  <label htmlFor="show-again-checkbox" className="cursor-pointer text-xs font-medium text-gray-700">
                    Show again
                  </label>
                  <p className="mt-0.5 text-xs text-gray-400">
                    Display all speaker cards again when a new speaker joins
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
);

export default TimingControls;
